import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { domainService, CreateDomainRequest, UpdateDomainRequest, DomainListParams } from '../../../services/domainService';
import { showToast } from '../../../stores/uiStore';
import { handleApiError } from '../../../services/api';
import { ROUTES } from '../../../config/routes';
import { getOperationErrorMessage } from '../../../config/errorMessages';
import { useSessionGate } from '../../../hooks/useSessionGate';

export const useDomains = (params?: DomainListParams) => {
  const { expired } = useSessionGate();
  return useQuery({
    queryKey: ['domains', params],
    queryFn: () => domainService.getDomains(params),
    enabled: !expired,
  });
};

export const useDomain = (id: string) => {
  const { expired } = useSessionGate();
  return useQuery({
    queryKey: ['domain', id],
    queryFn: () => domainService.getDomain(id),
    enabled: !!id && !expired,
    retry: 1,
    retryDelay: 1000,
    refetchInterval: (query) => {
      const d = query.state.data?.data;
      const syncing = d?.sync_status === 'syncing';
      const filesPending = d?.crawl_files?.some((f: any) => f.sync_status !== 'synced' && f.sync_status !== 'error');
      return (syncing || filesPending) ? 5000 : false;
    },
  });
};

export const useCreateDomain = (options?: { onSuccessOverride?: (data: any) => void }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: CreateDomainRequest) => domainService.createDomain(data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        if (response.data.api_key) {
          sessionStorage.setItem(`eb_apikey_${response.data.id}`, response.data.api_key);
        }
        queryClient.invalidateQueries({ queryKey: ['domains'] });
        if (options?.onSuccessOverride) {
          options.onSuccessOverride(response.data);
        } else {
          showToast.success('ドメインを作成しました');
          navigate(`/chatbots/${response.data.id}`);
        }
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('create'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useUpdateDomain = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateDomainRequest) => domainService.updateDomain(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['domain', id] });
        queryClient.invalidateQueries({ queryKey: ['domains'] });
        showToast.success('ドメイン設定を更新しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('update'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useDeleteDomain = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (id: string) => domainService.deleteDomain(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['domains'] });
        showToast.success('ドメインを削除しました');
        navigate(ROUTES.DOMAINS);
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('delete'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useRegenerateApiKey = (domainId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => domainService.regenerateApiKey(domainId),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['domain', domainId] });
        showToast.success('APIキーを再生成しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('update'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useUploadLogo = (domainId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => domainService.uploadLogo(domainId, file),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['domain', domainId] });
        showToast.success('ロゴをアップロードしました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('upload'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useDeleteLogo = (domainId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => domainService.deleteLogo(domainId),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['domain', domainId] });
        showToast.success('ロゴを削除しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('delete'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};
